import React from 'react';
import Container from '../common/Container';
import Card from '../common/Card';
import Button from '../common/Button';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  const handleBackToGame = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-dark-950">
      <Container>
        <div className="max-w-4xl mx-auto py-8">
          <Card>
            <div className="p-8">
              <h1 className="text-3xl font-bold text-primary-400 mb-6">Privacy Policy</h1>
              
              <div className="prose prose-gray max-w-none">
                <p className="text-dark-300 mb-6">
                  Last updated: {new Date().toLocaleDateString()}
                </p>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-primary-300 mb-4">1. Information We Collect</h2>
                  <p className="text-dark-200 mb-4">
                    The AI Guessing Game collects minimal information to provide and improve our service:
                  </p>
                  <ul className="list-disc list-inside text-dark-200 mb-4 ml-4">
                    <li><strong className="text-secondary-400">Game Data:</strong> Your game scores, selections, and gameplay statistics</li>
                    <li><strong className="text-secondary-400">Usage Data:</strong> Information about how you interact with the game</li>
                    <li><strong className="text-secondary-400">Technical Data:</strong> Browser type, device information, and IP address for security purposes</li>
                    <li><strong className="text-secondary-400">Discord Data:</strong> If playing through Discord, your Discord user ID and display name</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-primary-300 mb-4">2. How We Use Your Information</h2>
                  <p className="text-dark-200 mb-4">
                    We use the collected information for the following purposes:
                  </p>
                  <ul className="list-disc list-inside text-dark-200 mb-4 ml-4">
                  <li>To provide and maintain the game functionality</li>
                  <li>To track your progress and display your scores</li>
                  <li>To improve the game experience and content quality</li>
                  <li>To analyze usage patterns and optimize performance</li>
                  <li>To prevent fraud and ensure security</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-primary-300 mb-4">3. Information Sharing and Disclosure</h2>
                  <p className="text-dark-200 mb-4">
                    We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:
                  </p>
                  <ul className="list-disc list-inside text-dark-200 mb-4 ml-4">
                    <li><strong className="text-secondary-400">Service Providers:</strong> We may share information with trusted service providers who assist in operating our game</li>
                    <li><strong className="text-secondary-400">Legal Requirements:</strong> We may disclose information when required by law or to protect our rights</li>
                    <li><strong className="text-secondary-400">Aggregated Data:</strong> We may share anonymized, aggregated statistics that cannot identify individual users</li>
                </ul>
              </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-primary-300 mb-4">4. Data Storage and Security</h2>
                  <p className="text-dark-200 mb-4">
                    We implement appropriate security measures to protect your information:
                  </p>
                  <ul className="list-disc list-inside text-dark-200 mb-4 ml-4">
                  <li>Data is stored securely using industry-standard encryption</li>
                  <li>Access to personal data is restricted to authorized personnel only</li>
                  <li>We regularly review and update our security practices</li>
                  <li>Game data is stored for as long as necessary to provide the service</li>
                </ul>
              </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-primary-300 mb-4">5. Cookies and Local Storage</h2>
                  <p className="text-dark-200 mb-4">
                    The game may use browser local storage and cookies to:
                  </p>
                  <ul className="list-disc list-inside text-dark-200 mb-4 ml-4">
                  <li>Remember your game preferences and settings</li>
                  <li>Store your game progress and scores locally</li>
                  <li>Improve the game's performance and user experience</li>
                </ul>
                  <p className="text-dark-200 mb-4">
                    You can control cookie settings through your browser preferences, though this may affect game functionality.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-primary-300 mb-4">6. Third-Party Services</h2>
                  <p className="text-dark-200 mb-4">
                    The game may integrate with third-party services:
                  </p>
                  <ul className="list-disc list-inside text-dark-200 mb-4 ml-4">
                    <li><strong className="text-secondary-400">Discord:</strong> When played through Discord, Discord's privacy policy applies to data they collect</li>
                    <li><strong className="text-secondary-400">Analytics Services:</strong> We may use analytics tools to understand usage patterns</li>
                    <li><strong className="text-secondary-400">Content Delivery:</strong> We may use CDNs to deliver game content efficiently</li>
                </ul>
              </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-primary-300 mb-4">7. Children's Privacy</h2>
                  <p className="text-dark-200 mb-4">
                    The game is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-primary-300 mb-4">8. Your Rights</h2>
                  <p className="text-dark-200 mb-4">
                    Depending on your location, you may have the following rights regarding your personal information:
                  </p>
                  <ul className="list-disc list-inside text-dark-200 mb-4 ml-4">
                  <li>The right to access your personal information</li>
                  <li>The right to correct inaccurate information</li>
                  <li>The right to delete your personal information</li>
                  <li>The right to restrict processing of your information</li>
                  <li>The right to data portability</li>
                </ul>
              </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-primary-300 mb-4">9. Changes to This Privacy Policy</h2>
                  <p className="text-dark-200 mb-4">
                    We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-primary-300 mb-4">10. Contact Us</h2>
                  <p className="text-dark-200 mb-4">
                    If you have any questions about this Privacy Policy or our data practices, please contact us through the game interface or repository.
                  </p>
                </section>
              </div>

              <div className="mt-8 pt-6 border-t border-dark-700">
              <Button
                onClick={handleBackToGame}
                variant="primary"
                className="w-full sm:w-auto"
              >
                Back to Game
              </Button>
            </div>
          </div>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;